import { useEffect, useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'

export function useSharedCountdown(): {
  remainingTime: string
  isExpired: boolean
  startCountdown: () => void
  getRemainingSeconds: () => number
  getRemainingTime: () => number
} {
  const { userPreferences, setSessionState } = useSessionStateStore()
  const { config: globalConfig } = useConfigStore()
  const [remainingTime, setRemainingTime] = useState<string>('00:00')
  const [isExpired, setIsExpired] = useState(false)

  const captureTimeout = globalConfig?.pages.capture?.settings?.captureTimeout || 600
  const sharedCountdownStartTime = userPreferences.sharedCountdownStartTime
  const sharedCountdownDuration = userPreferences.sharedCountdownDuration || captureTimeout * 1000

  const getRemainingTime = (): number => {
    if (!sharedCountdownStartTime) return 0
    const elapsed = Date.now() - sharedCountdownStartTime
    return Math.max(0, sharedCountdownDuration - elapsed)
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    if (!sharedCountdownStartTime) {
      setRemainingTime('00:00')
      setIsExpired(false)
      return
    }

    const updateCountdown = (): void => {
      const remaining = getRemainingTime()
      const remainingSeconds = Math.floor(remaining / 1000)

      if (remainingSeconds <= 0) {
        setRemainingTime('00:00')
        setIsExpired(true)
      } else {
        setRemainingTime(formatTime(remainingSeconds))
        setIsExpired(false)
      }
    }

    // Update immediately
    updateCountdown()

    // Update every second
    const interval = setInterval(updateCountdown, 1000)

    return (): void => clearInterval(interval)
  }, [sharedCountdownStartTime, sharedCountdownDuration])

  const startCountdown = (): void => {
    const now = Date.now()
    setSessionState({
      userPreferences: {
        sharedCountdownStartTime: now,
        sharedCountdownDuration: captureTimeout * 1000
      }
    })
  }

  const getRemainingSeconds = (): number => {
    return Math.floor(getRemainingTime() / 1000)
  }

  return {
    remainingTime,
    isExpired,
    startCountdown,
    getRemainingSeconds,
    getRemainingTime
  }
}
